import { useEffect, useState } from 'react';
import { ArrowRightIcon, MailIcon, MessageCircleIcon, PencilIcon, PhoneIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { ContactRow } from './ContactRow';
import { CreditPanel } from './CreditPanel';
import { BookingBlockPanel } from './BookingBlockPanel';
import { useAdmin } from '../AdminContext';
import { attentionFor, attentionLabel, attentionTab, whatsappNumber, type CustomerTab } from './customerRules';
import { btnGhost, btnPrimary, btnSecondary, inputCls } from '../ui/styles';
import { useCan } from '../useCan';
import { ContactEditForm } from './ContactEditForm';
import { effectivePlan } from '../../utils/account';
import { useEditBuffer } from '../../hooks/useEditBuffer';
import { currentTip } from '../../utils/skinTips';
import { dayMedium } from '../../core/format/dates';
import { plural } from '../../core/format/text';
import { formatStreet, telHref } from '../../core/rules/contact';
import { acquisitionLabels } from '../../data/acquisition';
import type { CustomerFileControl } from './useCustomerFile';
import type { SourceAskedAt } from '../../core/types/insights';
type Props = {
  ctl: CustomerFileControl;
  onTab: (tab: CustomerTab) => void;
  onDirtyChange?: (dirty: boolean) => void;
};
const sourceVia: Record<SourceAskedAt, string> = {
  bestelling: 'na een bestelling',
  afspraak: 'bij het boeken',
  kassa: 'aan de kassa'
};

/** Eerste scherm van een dossier: wat er speelt, wat zij nu ziet, en hoe je haar bereikt. */
export function CustomerOverview({
  ctl,
  onTab,
  onDirtyChange
}: Props) {
  const {
    index,
    site
  } = useAdmin();
  const {
    account,
    internalNote
  } = ctl.file;
  const c = account.customer;
  const plan = effectivePlan(account, index.productById);
  const tip = currentTip(account, plan.items, index);
  const flags = attentionFor(account, site.policy.revisitAfterWeeks);
  const wa = whatsappNumber(c.phone);

  // Wat Sarah typt, blijft staan bij een verversing op de achtergrond en bij een mislukte opslag;
  // zonder getypte tekst volgt het veld de server (hooks/useEditBuffer.ts).
  const note = useEditBuffer(internalNote, c.id);
  const noteDirty = note.dirty;
  useEffect(() => onDirtyChange?.(noteDirty), [noteDirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  const {
    canAll
  } = useCan();
  const [editingContact, setEditingContact] = useState(false);
  const prefs = [c.prefs.refillWhatsApp && 'seintje via WhatsApp', c.prefs.refillEmail && 'seintje per mail', c.prefs.newsletter && 'nieuwsbrief'].filter(Boolean);
  return <div className="grid gap-6 lg:grid-cols-3" data-id="element-1212" data-mp="26p18a0cly1h8">
      <div className="min-w-0 space-y-6 lg:col-span-2" data-id="element-1213" data-mp="2kmzmn5qxhxwq">
        {flags.length > 0 && <Panel title="Vraagt je aandacht" flush data-id="element-1214" data-mp="2glnscmx40oi7">
            <ul className="divide-y divide-line" data-id="element-1215" data-mp="2gk2w8sv5cxi8">
              {flags.map(f => <li key={f} data-id="element-1216" data-mp="3w1d15jzsl103">
                  <button type="button" onClick={() => onTab(attentionTab[f])} className="flex w-full items-center gap-3 px-4 py-3.5 text-left text-[14px] text-ink transition-colors duration-150 ease-soft hover:bg-shell sm:px-5" data-id="element-1217" data-mp="3w0lb0ooyrvh7">
                    <span className="h-2 w-2 shrink-0 rounded-full bg-clay" aria-hidden="true" data-id="element-1218" data-mp="8boamw879dr4" />
                    <span className="flex-1" data-id="element-1219" data-mp="3po8t243rkyao">{attentionLabel[f]}</span>
                    <ArrowRightIcon className="h-4 w-4 text-muted" strokeWidth={1.5} aria-hidden="true" data-id="element-1220" data-mp="3ulr7ub4gq7ql" />
                  </button>
                </li>)}
            </ul>
          </Panel>}

        <Panel title={`Wat ${c.firstName} nu ziet in Mijn omgeving`} description="De tip van dit moment, zoals de site die kiest. De woorden pas je aan bij Paginateksten → Mijn omgeving." data-id="element-1221" data-mp="r3qushk1vc64">
          <div className="rounded-md bg-ink p-5 text-shell" data-id="element-1222" data-mp="hcconqxlfe40">
            <p className="font-display text-[20px] font-light leading-snug" data-id="element-1223" data-mp="153i5o9f9scfw">{tip.title}</p>
            <p className="mt-2 text-[14px] leading-relaxed text-shell/85" data-id="element-1224" data-mp="19w1rfto42dvs">{tip.body}</p>
          </div>
          <p className="mt-4 text-[13px] text-muted" data-id="element-1225" data-mp="3icugsbm9t8ov">
            {plan.items.length === 0 ? 'Nog geen routine: ze ziet uitleg om te beginnen.' : plan.provisional ? `Voorlopige routine uit haar bestellingen (${plural(plan.items.length, 'product', 'producten')}). ` : `Persoonlijk huidplan met ${plural(plan.items.length, 'product', 'producten')}. `}
            {plan.items.length > 0 && <button type="button" onClick={() => onTab('huidplan')} className="text-clay underline underline-offset-4" data-id="element-1226" data-mp="3w3hec0gaosjd">
                {plan.provisional ? 'Maak het persoonlijk' : 'Huidplan bekijken'}
              </button>}
          </p>
        </Panel>

        <Panel title="Interne notitie" description={`Alleen voor jou. ${c.firstName} ziet dit nooit.`} data-id="element-1227" data-mp="3mdedxf6ugcb0">
          <label className="sr-only" htmlFor="internal-note" data-id="element-1228" data-mp="npqyczkqssla">
            Interne notitie
          </label>
          <textarea id="internal-note" value={note.value} onChange={e => {
          const text = e.target.value;
          note.edit(() => text);
        }} rows={4} placeholder="Bijv. allergieën, zwangerschap, voorkeuren in de salon" className={`${inputCls} resize-y leading-relaxed`} data-id="element-1229" data-mp="iv4lerz0irz3" />
          {note.external && <p role="status" className="mt-2 text-[13px] text-clay" data-id="element-1230" data-mp="1a4fu0bnl7iw6">
              De notitie is intussen ergens anders aangepast. Bewaren zet jouw tekst eroverheen.{' '}
              <button type="button" onClick={note.discard} className="text-ink underline underline-offset-4" data-id="element-1231" data-mp="1e4844ssmpx3n">
                Neem die versie over
              </button>
            </p>}
          <div className="mt-3 flex gap-2" data-id="element-1232" data-mp="1nv2rk0hm32qo">
            <button type="button" onClick={() => void note.commit(ctl.saveNote)} disabled={!note.dirty || ctl.isBusy('note')} aria-busy={ctl.isBusy('note')} className={btnPrimary} data-id="element-1233" data-mp="1svh33sif8utg">
              {ctl.isBusy('note') ? 'Bewaren…' : 'Notitie bewaren'}
            </button>
          </div>
        </Panel>
      </div>

      <div className="min-w-0 space-y-6" data-id="element-1234" data-mp="1hvg01h8qstq0">
        <Panel title="Contact" actions={canAll('customers.edit') && !editingContact ? <button type="button" onClick={() => setEditingContact(true)} className={btnGhost} data-id="element-1236" data-mp="1d2wn8mhvq2m9">
                <PencilIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-1237" data-mp="1qygzx070lol8" />
                Bewerken
              </button> : null} data-id="element-1235" data-mp="1hx96avfg1bxh">
          {editingContact ? <ContactEditForm customer={c} onCancel={() => setEditingContact(false)} onSaved={() => {
          setEditingContact(false);
          ctl.reload();
        }} data-id="element-1238" data-mp="1vvh9vm6zjbhm" /> : <>
          <dl className="space-y-3 text-[14px]" data-id="element-1239" data-mp="1jamzq4qb69h">
            <ContactRow label="E-mail" data-id="element-1240" data-mp="2d49rzs9hpnnt">
              {c.email ? <a href={`mailto:${c.email}`} className="break-all text-ink hover:text-clay" data-id="element-1241" data-mp="2d75qpit5wgdb">
                  {c.email}
                </a> : <span className="text-muted" data-id="element-1242" data-mp="23e70xxlxijs4">Niet bekend · geen mails of inlog</span>}
            </ContactRow>
            <ContactRow label="Telefoon" data-id="element-1243" data-mp="28c05ynbhx7qt">{c.phone || <span className="text-muted" data-id="element-1244" data-mp="1udsegux8ayn1">Niet bekend</span>}</ContactRow>
            <ContactRow label="Adres" data-id="element-1245" data-mp="1ugoagdpind8u">
              {c.address.street ? <>
                  {formatStreet(c.address)}
                  <br data-id="element-1246" data-mp="1kh5u078te9lj" />
                  {c.address.postcode} {c.address.city}
                  {c.address.country === 'BE' && ' · België'}
                </> : <span className="text-muted" data-id="element-1247" data-mp="3etnb0x328dhq">Niet bekend</span>}
            </ContactRow>
            <ContactRow label="Klant sinds" data-id="element-1248" data-mp="3jtrqqkw5946w">{dayMedium(c.memberSince)}</ContactRow>
            <ContactRow label="Kwam via" data-id="element-1249" data-mp="26mdm4sofmvw2">
              {c.acquisitionSource ? <>
                  {acquisitionLabels[c.acquisitionSource.channel]}
                  {c.acquisitionSource.other && `: ${c.acquisitionSource.other}`}
                  <span className="block text-[12px] text-muted" data-id="element-1250" data-mp="1xsikvvqflplp">
                    Gezegd {sourceVia[c.acquisitionSource.via]}, {dayMedium(c.acquisitionSource.at)}
                  </span>
                </> : <span className="text-muted" data-id="element-1251" data-mp="1ntj9w6i71hvj">Niet bekend</span>}
            </ContactRow>
            <ContactRow label="Betaalt met" data-id="element-1252" data-mp="1iz5hmcytvqv3">{c.payment.label}</ContactRow>
            <ContactRow label="Wil graag" data-id="element-1253" data-mp="1evhdk49endel">{prefs.length ? prefs.join(', ') : <span className="text-muted" data-id="element-1254" data-mp="19ww7vpbdw1ir">Geen berichten</span>}</ContactRow>
          </dl>
          <div className="mt-5 grid grid-cols-2 gap-2 border-t border-line pt-4" data-id="element-1255" data-mp="399ry40iyvlek">
            {wa && <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className={btnPrimary} data-id="element-1256" data-mp="349vxmb9hac3r">
                <MessageCircleIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1257" data-mp="3bsn1sidcgm8r" />
                WhatsApp
                <span className="sr-only" data-id="element-1258" data-mp="3gr6o4jvuo01n"> met {c.firstName} (opent in een nieuw tabblad)</span>
              </a>}
            {c.phone && !wa && <a href={telHref(c.phone)} className={btnSecondary} data-id="element-1259" data-mp="3qj2m7uxxrq5v">
                <PhoneIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1260" data-mp="3qkdsc1euta05" />
                Bellen
              </a>}
            {c.email && <a href={`mailto:${c.email}`} className={btnSecondary} data-id="element-1261" data-mp="2xuf8qljfi52v">
                <MailIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1262" data-mp="32sqhsu1wgbk1" />
                Mail
              </a>}
          </div>
          </>}
        </Panel>
        <CreditPanel data-id="element-1263" data-mp="i3ao9rbaq8qp" />
        <BookingBlockPanel ctl={ctl} data-id="element-1264" data-mp="i1zg7fatiets" />
        {/* Wie het dossier hiervoor opende (het logboek): zo zie je of iemand anders erin keek. */}
        {ctl.file.lastViewed && <p className="px-1 text-[12px] text-muted" data-id="element-1265" data-mp="1jcwukj82zsbb">
            Laatst ingezien door {ctl.file.lastViewed.name}, {dayMedium(ctl.file.lastViewed.at.slice(0, 10))} om {ctl.file.lastViewed.at.slice(11, 16)}
          </p>}
      </div>
    </div>;
}